import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import SelectUser from "../../components/selectUser";
import DebounceInput from "../../components/DebounceInput";
import { getUserMonthlyOccupation, getAggregatedOccupationsForMonth, filterOccupationsForMonth, mapActivitiesToOccupations } from "../../utils/occupation";

const PROJECT_COLORS = [
  "#4f46e5", // Indigo
  "#06b6d4", // Cyan
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#f97316", // Orange
  "#14b8a6", // Teal
  "#8b5cf6", // Violet
  "#f43f5e", // Rose
  "#10b981", // Emerald
  "#6366f1", // Indigo (slightly different shade)
];

export default function ByPerson({ businessUnit }) {
  const [users, setUsers] = useState([]);
  const [freelanceDailyUsers, setFreelanceDailyUsers] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sortMonth, setSortMonth] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedManager, setSelectedManager] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    manageesOfManagees: false,
  });
  const months = [...Array(6)].map((_, i) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + i);
    return date;
  });

  useEffect(() => {
    loadData();
  }, [filters, selectedManager, businessUnit]);

  async function loadData() {
    const query = {
      contracts: [CONTRACTS.CDD, CONTRACTS.CDI, CONTRACTS.VIE, CONTRACTS.INTERNSHIP, CONTRACTS.APPRENTICESHIP, CONTRACTS.FREELANCE, CONTRACTS.ME_KOHE_TE_PLOTE],
      role: ["user", "admin", "super_admin"],
      status: "active",
      search: filters.search,
    };

    if (businessUnit._id !== "ALL") {
      query.business_unit_id = businessUnit._id;
    }

    if (selectedManager) {
      query.manager_id = selectedManager._id;
      query.managees_of_managees = filters.manageesOfManagees;
    }

    const { data } = await api.post("/user/search", query);
    const regularUsers = data.users.filter((user) => user.payment_type !== "DAILY");
    const freelanceDailyUsrs = data.users.filter((user) => user.contract === "FREELANCE" && user.payment_type === "DAILY");

    if (selectedManager) {
      if (!regularUsers.some((u) => u._id === selectedManager._id) && selectedManager.payment_type !== "DAILY") {
        regularUsers.push(selectedManager);
      }
      if (!freelanceDailyUsrs.some((u) => u._id === selectedManager._id) && selectedManager.contract === "FREELANCE" && selectedManager.payment_type === "DAILY") {
        freelanceDailyUsrs.push(selectedManager);
      }
    }

    setUsers(regularUsers);
    setFreelanceDailyUsers(freelanceDailyUsrs);

    // Load forecasted activities scoped to these users
    const allUsers = [...regularUsers, ...freelanceDailyUsrs];
    const userIds = allUsers.map((u) => u._id);
    if (userIds.length > 0) {
      const dateFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const { data: activities } = await api.post("/activity/search", { userIds, has_forecast: true, dateFrom });
      setOccupations(mapActivitiesToOccupations(activities));
    } else {
      setOccupations([]);
    }
  }

  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const getCurrentMonthOccupancy = (user, currentMonth) => {
    return getUserMonthlyOccupation(occupations, user._id, currentMonth);
  };

  const displayedUsers = (() => {
    let filtered = users;

    return sortMonth
      ? [...filtered].sort((a, b) => {
          const aOccupancy = getCurrentMonthOccupancy(a, sortMonth);
          const bOccupancy = getCurrentMonthOccupancy(b, sortMonth);
          return sortDirection === "desc" ? bOccupancy - aOccupancy : aOccupancy - bOccupancy;
        })
      : filtered;
  })();

  const handleSort = (month) => {
    const isSameMonth = sortMonth && month.getMonth() === sortMonth.getMonth() && month.getFullYear() === sortMonth.getFullYear();

    if (isSameMonth) {
      if (sortDirection === "desc") {
        setSortDirection("asc");
      } else {
        setSortMonth(null);
        setSortDirection("desc");
      }
      return;
    }

    setSortMonth(month);
    setSortDirection("desc");
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 space-y-3">
        <div className="col-span-3">
          <DebounceInput debounce={300} value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="input" placeholder="Search" />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.manageesOfManagees}
              onChange={(e) => setFilters({ ...filters, manageesOfManagees: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={!selectedManager}
            />
            <span>Include managees of managees</span>
          </label>
          <SelectUser className="w-full min-w-[200px]" value={selectedManager} onChange={(user) => setSelectedManager(user)} userRoles={["admin", "user", "super_admin"]} />
        </div>
        <div className="text-sm text-gray-600">
          Showing {displayedUsers.length} users{selectedProject ? ` working on ${selectedProject.name}` : ""}
          {selectedManager ? ` under ${selectedManager.name}` : ""}
        </div>
      </div>

      {/* Regular Users Table */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-3 text-gray-700">Regular Occupation</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-1 text-xs w-[120px]">Person</th>
                {months.map((month) => {
                  const totalPercentage = displayedUsers.length > 0 
                    ? displayedUsers.reduce((sum, user) => sum + getUserMonthlyOccupation(occupations, user._id, month), 0) / displayedUsers.length 
                    : 0;

                  const isCurrentSortMonth = sortMonth && month.getMonth() === sortMonth.getMonth() && month.getFullYear() === sortMonth.getFullYear();

                  return (
                    <th key={month.toString()} className="text-left p-1 text-xs min-w-[150px]" onClick={() => handleSort(month)} style={{ cursor: "pointer" }}>
                      <div className="flex items-center gap-1">
                        <div className="text-gray-400 mb-1">{totalPercentage.toFixed(0)}%</div>
                        {isCurrentSortMonth && <span className="text-blue-500">↓</span>}
                      </div>
                      {month.toLocaleString("default", { month: "short", year: "numeric" })}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayedUsers.map((user, index) => {
                const isInactive = user.exit_date && new Date() > new Date(user.exit_date);
                return (
                  <tr
                    key={user._id}
                    className={`border-b hover:bg-gray-200 hover:bg-opacity-50 cursor-pointer ${isInactive ? "bg-gray-100" : ""}`}
                    onClick={async () => {
                      query.set("user_modal_id", user._id);
                      query.set("user_modal_tab", "activity");
                      navigate({ search: query.toString() });
                    }}>
                    <td className="p-1">
                      <div className="text-sm font-medium">{user.name}</div>
                      <div className="text-[11px] text-gray-400 -mt-0.5 space-x-1">
                        <span>{user.position}</span>
                        <span>·</span>
                        <span>{user.office}</span>
                        <span>·</span>
                        <span>{user.contract}</span>
                      </div>
                    </td>
                    {months.map((month) => {
                      return (
                        <td key={month.toString()} className="p-1">
                          <OccupationBar
                            user={user}
                            month={month}
                            occupations={filterOccupationsForMonth(occupations, user._id, month)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Freelance Daily Users Section */}
      {freelanceDailyUsers.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-300">
          <h2 className="text-lg font-semibold mb-3 text-gray-700">Freelance Daily (Monthly Workload)</h2>
          <div className="text-sm text-gray-600 mb-3">
            Showing {freelanceDailyUsers.length} freelance daily users
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-1 text-xs w-[120px]">Person</th>
                  {months.map((month) => {
                    const totalPercentage = freelanceDailyUsers.length > 0 
                      ? freelanceDailyUsers.reduce((sum, user) => sum + getUserMonthlyOccupation(occupations, user._id, month), 0) / freelanceDailyUsers.length 
                      : 0;

                    return (
                      <th key={month.toString()} className="text-left p-1 text-xs min-w-[150px]">
                        <div className="flex items-center gap-1">
                          <div className="text-gray-400 mb-1">{totalPercentage.toFixed(0)}%</div>
                        </div>
                        {month.toLocaleString("default", { month: "short", year: "numeric" })}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {freelanceDailyUsers.map((user, index) => {
                  const isInactive = user.exit_date && new Date() > new Date(user.exit_date);
                  return (
                    <tr
                      key={user._id}
                      className={`border-b hover:bg-gray-200 hover:bg-opacity-50 cursor-pointer ${isInactive ? "bg-gray-100" : ""}`}
                      onClick={async () => {
                        query.set("user_modal_id", user._id);
                        query.set("user_modal_tab", "activity");
                        navigate({ search: query.toString() });
                      }}>
                      <td className="p-1">
                        <div className="text-sm font-medium">{user.name}</div>
                        <div className="text-[11px] text-gray-400 -mt-0.5 space-x-1">
                          <span>{user.position}</span>
                          <span>·</span>
                          <span>{user.office}</span>
                          <span>·</span>
                          <span className="font-semibold text-orange-600">FREELANCE DAILY</span>
                        </div>
                      </td>
                      {months.map((month) => {
                        return (
                          <td key={month.toString()} className="p-1">
                            <OccupationBar
                              user={user}
                              month={month}
                              occupations={filterOccupationsForMonth(occupations, user._id, month)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const OccupationBar = ({ user, month, occupations }) => {
  const isInactive = user?.exit_date && new Date(month).getTime() > new Date(user.exit_date).getTime();
  const incoming =
    user?.starting_date &&
    (new Date(month).getFullYear() < new Date(user.starting_date).getFullYear() ||
      (new Date(month).getFullYear() === new Date(user.starting_date).getFullYear() && new Date(month).getMonth() < new Date(user.starting_date).getMonth()));

  if (isInactive) return <div className="h-5 w-full overflow-hidden bg-gray-50 flex items-center justify-center text-gray-400 text-xs font-medium">inactive</div>;
  if (incoming) return <div className="h-5 w-full overflow-hidden bg-gray-50 flex items-center justify-center text-gray-400 text-xs font-medium">incoming</div>;

  // Use aggregation utility — now properly weights weekly entries by total weeks in month
  const aggregatedOccupations = getAggregatedOccupationsForMonth(occupations, month);
  const totalOccupancy = aggregatedOccupations.reduce((sum, o) => sum + o.percent, 0);
  const isOverallocated = totalOccupancy > 100;

  return (
    <div className="relative group">
      <div className={`h-5 w-full overflow-hidden bg-gray-50 rounded-sm flex ${isOverallocated ? "ring-2 ring-red-500" : ""}`}>
        {aggregatedOccupations.map((occupation, index) => (
          <div
            key={occupation.project_id || occupation.project_name}
            className="h-full transition-opacity hover:opacity-100 relative flex items-center justify-center flex-shrink-0"
            style={{
              width: `${(occupation.percent / Math.max(totalOccupancy, 100)) * 100}%`,
              backgroundColor: PROJECT_COLORS[index % PROJECT_COLORS.length],
              backgroundImage: occupation.soft ? "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.2) 5px, rgba(255,255,255,0.2) 10px)" : "none",
              opacity: occupation.hasWeekly ? 0.95 : 0.85,
              border: occupation.hasWeekly ? "1px solid rgba(168, 85, 247, 0.5)" : "none",
            }}
            title={
              occupation.isAveraged
                ? `${occupation.project_name}: ${occupation.percent.toFixed(0)}% (avg over ${occupation.totalWeeks} weeks, ${occupation.weeksCount} with entries)`
                : `${occupation.project_name}: ${occupation.percent.toFixed(0)}%`
            }>
            <span className="text-[10px] text-white font-medium z-10 absolute whitespace-nowrap">{occupation.percent.toFixed(0)}%</span>
          </div>
        ))}
      </div>
      {isOverallocated && (
        <div className="absolute left-0 -top-4 bg-white/90 border border-red-500 rounded px-1 text-[10px] text-red-500 whitespace-nowrap">
          {aggregatedOccupations.map((o) => `${o.project_name}: ${o.percent.toFixed(0)}%`).join(" + ")} = {totalOccupancy.toFixed(0)}%
        </div>
      )}
    </div>
  );
};

const CONTRACTS = {
  CDD: "CDD",
  CDI: "CDI",
  VIE: "VIE",
  INTERNSHIP: "INTERNSHIP",
  APPRENTICESHIP: "APPRENTICESHIP",
  FREELANCE: "FREELANCE",
  ME_KOHE_TE_PLOTE: "Me kohe te plote",
};
